import { rem, useToasts } from "@certn/cascadia";
import {
  BasicButton,
  BrandAsset,
  ButtonGroup,
  LegacyModal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PrimaryButton,
  Space,
  useMediaQuery,
} from "@certn/cascadia-react";
import { GhostIconButton } from "@certn/cascadia-react/components/button/GhostIconButton";
import { useGoogleAnalytics } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ApplicationContext } from "../pages/application/Application.context";
import { useNavbar } from "../pages/application/CheckFlowNavbar";
import { CheckEngineAPIServerError } from "../pages/application/errors";

type CheckFlowHeaderProps = {
  onMenuToggle: () => void;
};

export function CheckFlowHeader({ onMenuToggle }: CheckFlowHeaderProps) {
  const { t } = useTranslation();
  const { canToggle } = useNavbar();
  const { show } = useToasts();
  const { saveData, currentSectionKey } = useContext(ApplicationContext);
  const navigate = useNavigate();
  const { gaTrackEvent } = useGoogleAnalytics();
  const { mediaQuery } = useMediaQuery();

  const handleSaveAndExit = async () => {
    try {
      await saveData({ sectionKey: currentSectionKey });
      gaTrackEvent("application_save_and_exit", { section: currentSectionKey });
    } catch (e) {
      if (e instanceof CheckEngineAPIServerError) {
        show({ title: t("Something went wrong"), type: "error" });
        return;
      }
    }

    navigate("/");
  };

  return (
    <div
      className={css(
        styles.header,
        mediaQuery({
          paddingLeft: [rem("space-2"), rem("space-6")],
          paddingRight: [rem("space-2"), rem("space-6")],
        })
      )}
    >
      {canToggle && (
        <GhostIconButton
          size="medium"
          icon="list"
          color="icon-base"
          onPress={onMenuToggle}
        />
      )}
      <Link to="/">
        <BrandAsset height="32" width="110" type="full" brand="certn" />
      </Link>
      <Space />
      <ModalTrigger
        trigger={() => (
          <BasicButton size="medium">
            <Trans>Save & exit</Trans>
          </BasicButton>
        )}
      >
        {(close: () => void) => (
          <LegacyModal>
            <ModalHeader
              close={close}
              title={t("Are you sure you want to exit?")}
            />
            <ModalContent>
              <Trans>
                There are few steps remaining to complete your background check.
                Your progress up until now has been saved.
              </Trans>
            </ModalContent>
            <ModalFooter>
              <ButtonGroup>
                <BasicButton size="medium" onPress={close}>
                  <Trans>Cancel</Trans>
                </BasicButton>
                <PrimaryButton size="medium" onPress={handleSaveAndExit}>
                  <Trans>Save & exit</Trans>
                </PrimaryButton>
              </ButtonGroup>
            </ModalFooter>
          </LegacyModal>
        )}
      </ModalTrigger>
    </div>
  );
}

const styles = {
  header: css({
    alignItems: "center",
    display: "flex",
    gap: rem("space-4"),
    minHeight: rem("space-16"),
  }),
};
